// AcademicProjectStudentDashboard.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";

import GitHubIcon from "@mui/icons-material/GitHub";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import YouTubeIcon from "@mui/icons-material/YouTube";
import NotesIcon from "@mui/icons-material/Notes";
import SaveIcon from "@mui/icons-material/Save";
import AssignmentIcon from "@mui/icons-material/Assignment";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import GradingIcon from "@mui/icons-material/Grading";
import { API_URL } from "./config";

const AcademicProjectStudentDashboard = () => {
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const BLUE = "#1565C0";

  const pageWrapSx = { p: 2, backgroundColor: BG, minHeight: "100vh" };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
  };
  const bodySx = { p: 2, backgroundColor: BG };
  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: BORDER },
      "&:hover fieldset": { borderColor: BORDER_HOVER },
      "&.Mui-focused fieldset": { borderColor: BORDER_FOCUS },
    },
  };
  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const token = localStorage.getItem("auth") || localStorage.getItem("token");
  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const [projects, setProjects] = useState([]);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [availableAssignmentGroupFilter, setAvailableAssignmentGroupFilter] = useState("");

  // Top-level view: 0 = Assigned Projects, 1 = Available Projects.
  const [mainTab, setMainTab] = useState(0);
  // Detail tabs inside Assigned Projects.
  const [detailTab, setDetailTab] = useState(0);

  const [form, setForm] = useState({
    GitHubUrl: "",
    YouTubeUrl: "",
    NotionPromptUrl: "",
    DeploymentUrl: "",
    DemoUrl: "",
    StudentRemarks: "",
    ReadmeChecklistJson: {
      hasReadme: false,
      hasProblemStatement: false,
      hasFeatures: false,
      hasTechStack: false,
      hasExecutionSteps: false,
      hasEnvExample: false,
      hasScreenshots: false,
      hasDemoLink: false,
      hasPromptLog: false,
    },
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const statusColor = (status) => {
    const value = String(status || "").toUpperCase();
    if (value === "EVALUATED" || value === "COMPLETED") return "success";
    if (value === "SUBMITTED") return "info";
    if (value === "ASSIGNED" || value === "IN_PROGRESS") return "warning";
    if (value === "REJECTED" || value === "LATE" || value === "OVERDUE") return "error";
    return "default";
  };

  const formatDate = (value, includeTime = false) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value).slice(0, includeTime ? 19 : 10);
    return includeTime ? date.toLocaleString() : date.toLocaleDateString();
  };

  const isPastDue = (assignment) => {
    const endDate = String(assignment?.EndDate || "").slice(0, 10);
    if (!endDate) return false;

    const now = new Date();
    const today = [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, "0"),
      String(now.getDate()).padStart(2, "0"),
    ].join("-");

    return today > endDate;
  };

  const loadAssignment = (row) => {
    let checklist = row.ReadmeChecklistJson || {};
    if (typeof checklist === "string") {
      try {
        checklist = JSON.parse(checklist);
      } catch {
        checklist = {};
      }
    }

    setSelectedAssignment(row);
    setDetailTab(0);
    setForm({
      GitHubUrl: row.GitHubUrl || "",
      YouTubeUrl: row.YouTubeUrl || "",
      NotionPromptUrl: row.NotionPromptUrl || "",
      DeploymentUrl: row.DeploymentUrl || "",
      DemoUrl: row.DemoUrl || "",
      StudentRemarks: row.StudentRemarks || "",
      ReadmeChecklistJson: {
        hasReadme: !!checklist.hasReadme,
        hasProblemStatement: !!checklist.hasProblemStatement,
        hasFeatures: !!checklist.hasFeatures,
        hasTechStack: !!checklist.hasTechStack,
        hasExecutionSteps: !!checklist.hasExecutionSteps,
        hasEnvExample: !!checklist.hasEnvExample,
        hasScreenshots: !!checklist.hasScreenshots,
        hasDemoLink: !!checklist.hasDemoLink,
        hasPromptLog: !!checklist.hasPromptLog,
      },
    });
  };

  const fetchMyProjects = async () => {
    const res = await axios.get(`${API_URL}/api/academic-projects/student/my-projects`, {
      headers: authHeaders,
    });
    const rows = Array.isArray(res.data) ? res.data : [];
    setProjects(rows);

    setSelectedAssignment((current) => {
      if (!rows.length) return null;
      const refreshed = current
        ? rows.find((x) => x.AssignmentID === current.AssignmentID)
        : null;
      const next = refreshed || rows[0];

      // Refresh the editable form after submit/evaluation reload.
      let checklist = next.ReadmeChecklistJson || {};
      if (typeof checklist === "string") {
        try {
          checklist = JSON.parse(checklist);
        } catch {
          checklist = {};
        }
      }
      setForm({
        GitHubUrl: next.GitHubUrl || "",
        YouTubeUrl: next.YouTubeUrl || "",
        NotionPromptUrl: next.NotionPromptUrl || "",
        DeploymentUrl: next.DeploymentUrl || "",
        DemoUrl: next.DemoUrl || "",
        StudentRemarks: next.StudentRemarks || "",
        ReadmeChecklistJson: {
          hasReadme: !!checklist.hasReadme,
          hasProblemStatement: !!checklist.hasProblemStatement,
          hasFeatures: !!checklist.hasFeatures,
          hasTechStack: !!checklist.hasTechStack,
          hasExecutionSteps: !!checklist.hasExecutionSteps,
          hasEnvExample: !!checklist.hasEnvExample,
          hasScreenshots: !!checklist.hasScreenshots,
          hasDemoLink: !!checklist.hasDemoLink,
          hasPromptLog: !!checklist.hasPromptLog,
        },
      });
      return next;
    });
  };

  const fetchAvailableProjects = async () => {
    const res = await axios.get(
      `${API_URL}/api/academic-projects/student/available-projects`,
      { headers: authHeaders }
    );
    setAvailableProjects(Array.isArray(res.data) ? res.data : []);
  };

  const refreshDashboard = async () => {
    setLoading(true);
    setError("");
    try {
      await Promise.all([fetchMyProjects(), fetchAvailableProjects()]);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch academic projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    refreshDashboard();
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleChecklistChange = (name, checked) => {
    setForm((prev) => ({
      ...prev,
      ReadmeChecklistJson: {
        ...prev.ReadmeChecklistJson,
        [name]: checked,
      },
    }));
  };

  const validateSubmit = () => {
    if (!selectedAssignment?.AssignmentID) return "Select a project";
    if (!form.GitHubUrl) return "GitHub repository link is required";
    if (!form.YouTubeUrl) return "YouTube video presentation link is required";
    if (!form.NotionPromptUrl) return "Notion LLM prompt log link is required";
    if (!form.DeploymentUrl) return "Deployment link is required";
    if (!form.ReadmeChecklistJson.hasReadme) return "Confirm that GitHub README exists";
    if (!form.ReadmeChecklistJson.hasExecutionSteps) return "README must include execution steps";
    return "";
  };

  const handleSubmitProject = async () => {
    if (isPastDue(selectedAssignment)) {
      setError("The project due date has passed. This submission can no longer be edited.");
      return;
    }

    const validation = validateSubmit();
    if (validation) {
      setError(validation);
      return;
    }

    setSaving(true);
    setError("");
    try {
      await axios.put(
        `${API_URL}/api/academic-projects/student/my-projects/${selectedAssignment.AssignmentID}/submit`,
        form,
        { headers: authHeaders }
      );
      setSuccess("Project submitted successfully");
      await refreshDashboard();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to submit project");
    } finally {
      setSaving(false);
    }
  };

  const checklistItems = [
    ["hasReadme", "README.md file exists in GitHub repository"],
    ["hasProblemStatement", "README includes problem statement"],
    ["hasFeatures", "README includes features/modules"],
    ["hasTechStack", "README includes tech stack"],
    ["hasExecutionSteps", "README includes installation and execution steps"],
    ["hasEnvExample", "README includes .env.example or configuration notes"],
    ["hasScreenshots", "README includes screenshots/output images"],
    ["hasDemoLink", "README includes deployment/demo link"],
    ["hasPromptLog", "README or Notion includes LLM prompts used"],
  ];

  const renderAssignedProjectsView = () => {
    if (!projects.length) {
      return (
        <Paper elevation={0} sx={{ ...surfaceSx, p: 3 }}>
          <Typography sx={{ color: "text.secondary" }}>
            No academic projects assigned yet.
          </Typography>
        </Paper>
      );
    }

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Stack spacing={1.5}>
            {projects.map((row) => (
              <Paper
                key={row.AssignmentID}
                elevation={0}
                onClick={() => loadAssignment(row)}
                sx={{
                  ...surfaceSx,
                  p: 2,
                  cursor: "pointer",
                  borderColor:
                    selectedAssignment?.AssignmentID === row.AssignmentID
                      ? THEME_ORANGE
                      : BORDER,
                  boxShadow:
                    selectedAssignment?.AssignmentID === row.AssignmentID
                      ? "0 0 0 1px rgba(240,120,40,0.15)"
                      : "none",
                }}
              >
                <Typography sx={{ fontWeight: 900, color: BLUE }}>
                  {row.ProjectTitle}
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                  <Chip size="small" label={row.Domain || "-"} />
                  <Chip
                    size="small"
                    color={statusColor(row.StudentStatus)}
                    label={row.StudentStatus || "ASSIGNED"}
                  />
                </Stack>

                <Typography sx={{ mt: 1, color: "text.secondary", fontSize: "0.85rem" }}>
                  Due: {formatDate(row.EndDate)}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={surfaceSx}>
            <Tabs
              value={detailTab}
              onChange={(_, v) => setDetailTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                px: 1,
                borderBottom: `1px solid ${BORDER}`,
                "& .MuiTab-root": { textTransform: "none", fontWeight: 800 },
                "& .Mui-selected": { color: THEME_ORANGE },
                "& .MuiTabs-indicator": { backgroundColor: THEME_ORANGE },
              }}
            >
              <Tab label="Project Details" icon={<AssignmentIcon />} iconPosition="start" />
              <Tab label="Submission" icon={<GitHubIcon />} iconPosition="start" />
              <Tab label="Structure Guide" icon={<NotesIcon />} iconPosition="start" />
              <Tab label="Grade & Remarks" icon={<GradingIcon />} iconPosition="start" />
            </Tabs>

            {detailTab === 0 && selectedAssignment && (
              <Box sx={{ p: 2 }}>
                <Typography sx={{ fontWeight: 900, color: BLUE, fontSize: "1.2rem" }}>
                  {selectedAssignment.ProjectTitle}
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1, mb: 2 }}>
                  <Chip label={selectedAssignment.Domain || "-"} />
                  <Chip label={selectedAssignment.DifficultyLevel || "-"} />
                  <Chip label={selectedAssignment.TechStack || "-"} />
                  <Chip
                    color={statusColor(selectedAssignment.StudentStatus)}
                    label={selectedAssignment.StudentStatus || "ASSIGNED"}
                  />
                </Stack>

                <Typography sx={{ fontWeight: 900, mb: 0.5 }}>Problem Statement</Typography>
                <Typography sx={{ color: "text.secondary", mb: 2 }}>
                  {selectedAssignment.ProblemStatement || "-"}
                </Typography>

                <Typography sx={{ fontWeight: 900, mb: 0.5 }}>Expected Outcome</Typography>
                <Typography sx={{ color: "text.secondary", mb: 2 }}>
                  {selectedAssignment.ExpectedOutcome || "-"}
                </Typography>

                <Typography sx={{ fontWeight: 900, mb: 0.5 }}>Description</Typography>
                <Typography sx={{ color: "text.secondary", mb: 2 }}>
                  {selectedAssignment.ProjectDescription || "-"}
                </Typography>

                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontWeight: 900 }}>Start Date</Typography>
                    <Typography color="text.secondary">{formatDate(selectedAssignment.StartDate)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontWeight: 900 }}>Due Date</Typography>
                    <Typography color="text.secondary">{formatDate(selectedAssignment.EndDate)}</Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            {detailTab === 1 && selectedAssignment && (
              <Box sx={{ p: 2 }}>
                {isPastDue(selectedAssignment) && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    The due date ({formatDate(selectedAssignment.EndDate)}) has passed.
                    This project is now read-only and can no longer be submitted or updated.
                  </Alert>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="GitHub Repository Link"
                      name="GitHubUrl"
                      disabled={isPastDue(selectedAssignment)}
                      value={form.GitHubUrl}
                      onChange={handleChange}
                      fullWidth
                      size="small"
                      sx={inputSx}
                      InputProps={{
                        startAdornment: <GitHubIcon fontSize="small" sx={{ mr: 1, color: BLUE }} />,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="YouTube Video Presentation Link"
                      name="YouTubeUrl"
                      disabled={isPastDue(selectedAssignment)}
                      value={form.YouTubeUrl}
                      onChange={handleChange}
                      fullWidth
                      size="small"
                      sx={inputSx}
                      InputProps={{
                        startAdornment: <YouTubeIcon fontSize="small" sx={{ mr: 1, color: BLUE }} />,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="Notion LLM Prompt Log Link"
                      name="NotionPromptUrl"
                      disabled={isPastDue(selectedAssignment)}
                      value={form.NotionPromptUrl}
                      onChange={handleChange}
                      fullWidth
                      size="small"
                      sx={inputSx}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="Project Deployment Link"
                      name="DeploymentUrl"
                      disabled={isPastDue(selectedAssignment)}
                      value={form.DeploymentUrl}
                      onChange={handleChange}
                      fullWidth
                      size="small"
                      sx={inputSx}
                      InputProps={{
                        startAdornment: <RocketLaunchIcon fontSize="small" sx={{ mr: 1, color: BLUE }} />,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="Student Remarks"
                      name="StudentRemarks"
                      disabled={isPastDue(selectedAssignment)}
                      value={form.StudentRemarks}
                      onChange={handleChange}
                      fullWidth
                      multiline
                      minRows={3}
                      sx={inputSx}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
                      <Typography sx={{ fontWeight: 900, color: BLUE, mb: 1 }}>
                        GitHub README Checklist
                      </Typography>
                      <Grid container spacing={1}>
                        {checklistItems.map(([key, label]) => (
                          <Grid item xs={12} sm={6} key={key}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!form.ReadmeChecklistJson[key]}
                                  disabled={isPastDue(selectedAssignment)}
                                  onChange={(e) =>
                                    handleChecklistChange(key, e.target.checked)
                                  }
                                />
                              }
                              label={label}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      sx={primaryBtnSx}
                      startIcon={
                        saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />
                      }
                      onClick={handleSubmitProject}
                      disabled={saving || isPastDue(selectedAssignment)}
                    >
                      {isPastDue(selectedAssignment) ? "Due Date Passed" : "Submit / Update Project"}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            )}

            {detailTab === 2 && (
              <Box sx={{ p: 2 }}>
                <Typography sx={{ fontWeight: 900, color: BLUE, mb: 2 }}>
                  Recommended Project Structure
                </Typography>

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "#fafafa",
                    border: `1px solid ${BORDER}`,
                    fontFamily: "monospace",
                    whiteSpace: "pre-wrap",
                  }}
                >
{`project-name/
├── README.md
├── frontend/
│   ├── package.json
│   └── src/
├── backend/
│   ├── package.json
│   ├── .env.example
│   └── src/
├── database/
│   └── schema.sql
├── docs/
│   ├── screenshots/
│   └── architecture.png
└── presentation/
    └── demo-video-link.txt`}
                </Paper>

                <Divider sx={{ my: 2 }} />
                <Typography sx={{ fontWeight: 900, color: BLUE, mb: 1 }}>
                  README.md must include
                </Typography>
                <Stack spacing={1}>
                  <Typography>1. Project title and domain</Typography>
                  <Typography>2. Problem statement</Typography>
                  <Typography>3. Features/modules</Typography>
                  <Typography>4. Tech stack</Typography>
                  <Typography>5. Installation steps</Typography>
                  <Typography>6. Execution steps</Typography>
                  <Typography>7. Environment variables / .env.example</Typography>
                  <Typography>8. Screenshots or output images</Typography>
                  <Typography>9. Deployment link</Typography>
                  <Typography>10. YouTube presentation link</Typography>
                  <Typography>11. Notion link with LLM prompts used</Typography>
                </Stack>
              </Box>
            )}

            {detailTab === 3 && selectedAssignment && (
              <Box sx={{ p: 2 }}>
                <Typography sx={{ fontWeight: 900, color: BLUE, fontSize: "1.1rem", mb: 2 }}>
                  Evaluation / Grade & Remarks
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Paper elevation={0} sx={{ ...surfaceSx, p: 2, height: "100%" }}>
                      <Typography sx={{ fontWeight: 900, mb: 1 }}>Evaluation Status</Typography>
                      <Chip
                        color={statusColor(selectedAssignment.StudentStatus)}
                        label={selectedAssignment.StudentStatus || "NOT EVALUATED"}
                      />
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Paper elevation={0} sx={{ ...surfaceSx, p: 2, height: "100%" }}>
                      <Typography sx={{ fontWeight: 900, mb: 1 }}>Grade / Marks</Typography>
                      <Typography sx={{ fontSize: "1.5rem", fontWeight: 900, color: BLUE }}>
                        {selectedAssignment.Marks ?? "-"}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Paper elevation={0} sx={{ ...surfaceSx, p: 2, height: "100%" }}>
                      <Typography sx={{ fontWeight: 900, mb: 1 }}>Evaluated On</Typography>
                      <Typography color="text.secondary">
                        {formatDate(selectedAssignment.EvaluatedDateTime, true)}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
                      <Typography sx={{ fontWeight: 900, color: BLUE, mb: 1 }}>
                        Teacher Remarks
                      </Typography>
                      <Typography sx={{ whiteSpace: "pre-wrap", color: "text.secondary" }}>
                        {selectedAssignment.TeacherRemarks || "No teacher remarks yet."}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
                      <Typography sx={{ fontWeight: 900, color: BLUE, mb: 1 }}>
                        Your Submission Remarks
                      </Typography>
                      <Typography sx={{ whiteSpace: "pre-wrap", color: "text.secondary" }}>
                        {selectedAssignment.StudentRemarks || "-"}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    );
  };

  const availableAssignmentGroupOptions = useMemo(
    () =>
      [...new Set(
        availableProjects
          .map((row) => String(row.AssignmentGroupCode || "").trim())
          .filter(Boolean)
      )].sort(),
    [availableProjects]
  );

  const filteredAvailableProjects = useMemo(
    () =>
      availableAssignmentGroupFilter
        ? availableProjects.filter(
            (row) => String(row.AssignmentGroupCode || "") === availableAssignmentGroupFilter
          )
        : availableProjects,
    [availableProjects, availableAssignmentGroupFilter]
  );

  const renderAvailableProjectsView = () => (
    <Box>
      <Typography sx={{ fontWeight: 900, color: BLUE, mb: 0.5 }}>
        Available Projects for My Class
      </Typography>
      <Typography sx={{ color: "text.secondary", mb: 2 }}>
        These are published projects mapped to your current class and section. Assignment is still done by your class teacher.
      </Typography>

      <Paper elevation={0} sx={{ ...surfaceSx, p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              fullWidth
              size="small"
              label="Assignment Group"
              value={availableAssignmentGroupFilter}
              onChange={(e) => setAvailableAssignmentGroupFilter(e.target.value)}
              sx={inputSx}
            >
              <MenuItem value="">All Assignment Groups</MenuItem>
              {availableAssignmentGroupOptions.map((group) => (
                <MenuItem key={group} value={group}>
                  {group}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={8}>
            <Typography sx={{ color: "text.secondary", fontSize: "0.88rem" }}>
              Showing {filteredAvailableProjects.length} of {availableProjects.length} available projects
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {!filteredAvailableProjects.length ? (
        <Paper elevation={0} sx={{ ...surfaceSx, p: 3 }}>
          <Typography color="text.secondary">
            {availableProjects.length
              ? "No projects match the selected assignment group."
              : "No published projects are currently available for your class."}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filteredAvailableProjects.map((row) => (
            <Grid item xs={12} md={6} key={row.ProjectID}>
              <Paper elevation={0} sx={{ ...surfaceSx, p: 2, height: "100%" }}>
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  gap={1}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 900, color: BLUE, fontSize: "1.05rem" }}>
                      {row.ProjectTitle}
                    </Typography>
                    <Typography sx={{ color: "text.secondary", fontSize: "0.82rem", mt: 0.25 }}>
                      {row.AssignmentGroupCode || "-"} · Project #{row.ProjectNumber || "-"}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    color={Number(row.IsAssignedToMe) ? "success" : "default"}
                    label={Number(row.IsAssignedToMe) ? "Assigned to Me" : "Available"}
                  />
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ my: 1.5 }}>
                  <Chip size="small" label={row.Domain || "-"} />
                  <Chip size="small" label={row.DifficultyLevel || "-"} />
                  <Chip
                    size="small"
                    label={`${Number(row.AssignedCount || 0)}/${Number(row.MaxStudentsAllowed || 0)} assigned`}
                  />
                </Stack>

                <Typography sx={{ fontWeight: 900, mb: 0.5 }}>Problem Statement</Typography>
                <Typography sx={{ color: "text.secondary", mb: 1.5 }}>
                  {row.ProblemStatement || "-"}
                </Typography>

                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: "0.82rem", color: "text.secondary" }}>
                      Tech Stack
                    </Typography>
                    <Typography sx={{ fontWeight: 700 }}>{row.TechStack || "-"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: "0.82rem", color: "text.secondary" }}>
                      Due Date
                    </Typography>
                    <Typography sx={{ fontWeight: 700 }}>{formatDate(row.EndDate)}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Typography sx={{ fontSize: "1.1rem", fontWeight: 900 }}>
            My Academic Projects
          </Typography>
          <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
            View available projects, work on assigned projects, submit links, and review evaluation feedback
          </Typography>
        </Box>

        <Box sx={bodySx}>
          {!!error && (
            <Alert severity="error" onClose={() => setError("")} sx={{ borderRadius: 2, mb: 2 }}>
              {error}
            </Alert>
          )}

          {!!success && (
            <Snackbar open autoHideDuration={2500} onClose={() => setSuccess("")}>
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                {success}
              </Alert>
            </Snackbar>
          )}

          <Paper elevation={0} sx={{ ...surfaceSx, mb: 2 }}>
            <Tabs
              value={mainTab}
              onChange={(_, v) => setMainTab(v)}
              sx={{
                px: 1,
                "& .MuiTab-root": { textTransform: "none", fontWeight: 900 },
                "& .Mui-selected": { color: THEME_ORANGE },
                "& .MuiTabs-indicator": { backgroundColor: THEME_ORANGE },
              }}
            >
              <Tab label="Assigned Project" icon={<AssignmentIcon />} iconPosition="start" />
              <Tab label="Available Projects" icon={<FolderOpenIcon />} iconPosition="start" />
            </Tabs>
          </Paper>

          {loading ? (
            <Paper elevation={0} sx={{ ...surfaceSx, p: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={20} />
                <Typography>Loading projects...</Typography>
              </Stack>
            </Paper>
          ) : mainTab === 0 ? (
            renderAssignedProjectsView()
          ) : (
            renderAvailableProjectsView()
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default AcademicProjectStudentDashboard;
